import React, { useEffect, useRef, useState } from 'react';

const BOTTOM_STORAGE_KEY = 'doctorMascotBottom';
const HIDDEN_STORAGE_KEY = 'doctorMascotHidden';

const DoctorMascot: React.FC = () => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const closeBtnRef = useRef<HTMLButtonElement>(null);
    const draggingRef = useRef(false);
    const startYRef = useRef(0);
    const startBottomRef = useRef(0);

    const [hidden, setHidden] = useState(() => localStorage.getItem(HIDDEN_STORAGE_KEY) === 'true');
    const [bottom, setBottom] = useState<number>(() => {
        const saved = localStorage.getItem(BOTTOM_STORAGE_KEY);
        return saved ? parseFloat(saved) : 20;
    });

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        const getBottom = () => parseFloat(getComputedStyle(wrapper).bottom);

        const handlePointerDown = (e: PointerEvent) => {
            if (e.target === closeBtnRef.current) return;
            draggingRef.current = true;
            startYRef.current = e.clientY;
            startBottomRef.current = getBottom();
            wrapper.setPointerCapture(e.pointerId);
        };

        const handlePointerMove = (e: PointerEvent) => {
            if (!draggingRef.current) return;
            const deltaY = startYRef.current - e.clientY;
            let newBottom = startBottomRef.current + deltaY;
            const maxBottom = window.innerHeight - wrapper.offsetHeight - 10;
            newBottom = Math.max(10, Math.min(newBottom, maxBottom));
            setBottom(newBottom);
        };

        const handlePointerUp = (e: PointerEvent) => {
            if (!draggingRef.current) return;
            draggingRef.current = false;
            wrapper.releasePointerCapture(e.pointerId);
            localStorage.setItem(BOTTOM_STORAGE_KEY, String(getBottom()));
        };

        const handlePointerCancel = () => {
            draggingRef.current = false;
        };

        wrapper.addEventListener('pointerdown', handlePointerDown);
        wrapper.addEventListener('pointermove', handlePointerMove);
        wrapper.addEventListener('pointerup', handlePointerUp);
        wrapper.addEventListener('pointercancel', handlePointerCancel);

        return () => {
            wrapper.removeEventListener('pointerdown', handlePointerDown);
            wrapper.removeEventListener('pointermove', handlePointerMove);
            wrapper.removeEventListener('pointerup', handlePointerUp);
            wrapper.removeEventListener('pointercancel', handlePointerCancel);
        };
        // Wrapper element is re-created when toggling `hidden`, so listeners must reattach each time
    }, [hidden]);

    const handleClose = (e: React.MouseEvent) => {
        e.stopPropagation();
        setHidden(true);
        localStorage.setItem(HIDDEN_STORAGE_KEY, 'true');
    };

    const handleShow = () => {
        setHidden(false);
        localStorage.removeItem(HIDDEN_STORAGE_KEY);
    };

    if (hidden) {
        return (
            <button
                id="doctor-mascot-show"
                aria-label="Show doctor mascot"
                onClick={handleShow}
            >
                👨‍⚕️
            </button>
        );
    }

    return (
        <div id="doctor-mascot-wrapper" ref={wrapperRef} style={{ bottom }}>
            <img
                id="doctor-mascot"
                src="/img/doctor-mascot.png"
                alt="Helpful doctor mascot"
                draggable="false"
            />

            <button id="doctor-close" aria-label="Hide doctor mascot" ref={closeBtnRef} onClick={handleClose}>
                ×
            </button>
        </div>
    );
};

export default DoctorMascot;
